/**
 * A scroll viewer that paints nothing of its own.
 *
 * WEBWIN'S `showBorder: false` DOES NOT MEAN TRANSPARENT. `ScrollViewer.onPaint` is
 * `if (showBorder) theme.drawPanel(...) else rc.fillRect(rect, palette.fieldBg)` - so a borderless
 * scroller fills its whole viewport with the theme's FIELD background, which in this palette is the
 * card colour. That is right for a scrolling text field on a form, which is what the control was
 * built for, and wrong for both of the places this app scrolls:
 *
 *   * THE NAVIGATION RAIL. The rail is a navy `Card`; the nav list scrolls inside it. The scroller
 *     painted white over the navy, so every entry's pale-grey label sat on white and was barely
 *     legible - and the SELECTED entry, whose label is pure white by design, disappeared completely.
 *     That is the symptom Phase 0 recorded as "the selected rail entry's label is missing" and put
 *     down to a glyph-atlas artifact; it is neither an atlas nor the label. The text was always
 *     drawn, in white, on a white rectangle that should not have been there.
 *   * EVERY PAGE. `PageRoot` repaints the page's darker `pageBackground` precisely so white cards
 *     read as raised surfaces on it - and then the page's own scroller filled the card colour back
 *     over it, flattening the whole design to white-on-white with hairline borders.
 *
 * Painting nothing is the fix rather than painting the right colour, because what is behind differs
 * by site (navy in the rail, `pageBackground` on a page) and both of those already paint themselves.
 */
import { ScrollViewer, type ScrollViewerOptions } from 'webwin';

export class CrmScroller extends ScrollViewer {
  /** Transparent: whatever this scroller sits on has already painted itself. */
  protected override onPaint(): void {}
}

/**
 * A scroller that is only as tall as it needs to be, up to a cap.
 *
 * The device page has six lists that the XAML bounded with `MaxHeight` - installed software, pending
 * updates, patches, install jobs, files, events - so that one 250-row list cannot push the four
 * sections below it off the bottom of the page. `MaxHeight` has no Webwin equivalent: a
 * `ScrollViewer` does not implement `preferredSize` at all, so it is whatever height it is given and
 * an `autoHeight` leaves it at the constructor default with the list clipped inside it.
 *
 * So the height is set explicitly, from the content, after the content changes - which is what
 * `fit()` is for. Below the cap the scroller is exactly its list and never scrolls; above it, it
 * stops growing and scrolls internally. A page-level scroller alone would not do: six unbounded
 * lists is a page nobody can navigate.
 */
export class BoundedScroller extends CrmScroller {
  readonly maxHeight: number;

  constructor(maxHeight: number, options: ScrollViewerOptions = {}) {
    super({
      hAlign: 'stretch',
      horizontalScroll: 'never',
      verticalScroll: 'auto',
      showBorder: false,
      zoomEnabled: false,
      height: 1,
      ...options
    });
    this.maxHeight = maxHeight;
  }

  /** Call after the content changed. Lays the content out first, so the extent is the new one. */
  fit(): void {
    this.content.layout();
    const wanted = this.content.extent().height;
    // Never zero: a zero-height scroller is one a later `fit` can never measure, because its
    // content has no width to wrap into.
    this.height = Math.max(1, Math.min(this.maxHeight, wanted));
  }
}
